import {Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild} from "@angular/core";
import {FormControl, FormGroup} from "@angular/forms";
import {Subscription} from "rxjs";
import {GefConnectionDto} from "../../../../../../core/dtos/gef/gef-connection-dto";
import {SecteursService} from "../../../../../../core/services/entities/secteurs.service";
import {Auth2Service} from "../../../../../../core/services/security/auth2.service";
import {EnvironnementDTO} from "../../../../../../core/dtos/environnement-dto";
import {LookUpComponent} from "../../../../../../shared/ui/lookup/lookup.component";
import {FACTURE_EXTERNE_STATUT_LABELS, FactureExterneStatutEnum} from "../../../../../../core/enums/facturation/fournisseur/facture-externe-statut-enum";
import {AVOIR_EXTERNE_STATUT_LABELS, AvoirExterneStatutEnum} from "../../../../../../core/enums/facturation/fournisseur/avoir-externe-statut-enum";

@Component({
  selector: 'yo-gef-dol-api-form',
  templateUrl: './gef-dol-api-form.component.html',
  standalone: false
})
export class GefDolApiFormComponent implements OnInit, OnChanges, OnDestroy {

  @Input() gefConnection: GefConnectionDto;
  @Input() canModify: boolean;

  @ViewChild('environnementLookup') environnementLookup: LookUpComponent;

  secteurList: any[] = [];
  selectedSecteurs: number[] = [];
  environnementList: EnvironnementDTO[] = [];
  idSelectedEnvironnement: number;
  statutFactureExterneList: { code: string, libelle: string }[] = Object.values(FactureExterneStatutEnum)
    .map(code => ({code, libelle: FACTURE_EXTERNE_STATUT_LABELS[code]}));
  selectedStatutFactureExterneList: string[] = [];
  statutAvoirExterneList: { code: string, libelle: string }[] = Object.values(AvoirExterneStatutEnum)
    .map(code => ({code, libelle: AVOIR_EXTERNE_STATUT_LABELS[code]}));
  selectedStatutAvoirExterneList: string[] = [];
  form: FormGroup;

  private subSecteurList: Subscription;

  constructor(private secteursService: SecteursService,
              private auth2Svc: Auth2Service) {}

  ngOnInit(): void {
    this.loadSecteurs();
    this.loadEnvironnements();
    this.initForm();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['gefConnection']) {
      this.initForm();
      this.initSelectedSecteurs();
      this.initSelectedEnvironnement();
      this.initSelectedStatutFactureExterneList();
      this.initSelectedStatutAvoirExterneList();
    }
  }

  ngOnDestroy(): void {
    this.subSecteurList?.unsubscribe();
  }

  loadSecteurs(): void {
    this.subSecteurList = this.secteursService.getSecteurs().subscribe(result => {
      this.secteurList = result.allSecteurs;
      this.initSelectedSecteurs();
    });
  }

  loadEnvironnements(): void {
    this.environnementList = this.auth2Svc.arrayEnvironnementDTOs;
    this.initSelectedEnvironnement();
  }

  initSelectedEnvironnement(): void {
    const code = this.gefConnection?.properties?.importSiteProperties?.environnement;
    this.idSelectedEnvironnement = this.environnementList.find(e => e.code === code)?.id;
  }

  onChangeEnvironnement = ($event: any): void => {
    this.idSelectedEnvironnement = $event;
  }

  initForm(): void {
    this.form = new FormGroup({
      codeFamilleDenree: new FormControl(this.gefConnection?.properties?.importDenreeProperties?.famille),
      codeUTDenree: new FormControl(this.gefConnection?.properties?.importDenreeProperties?.uniteTechnique),
      codeTypeProduitDenree: new FormControl(this.gefConnection?.properties?.importDenreeProperties?.typeProduit),
      codeZoneStockageDenree: new FormControl(this.gefConnection?.properties?.importDenreeProperties?.zoneStockage),
      codeTypeApproDenree: new FormControl(this.gefConnection?.properties?.importDenreeProperties?.typeAppro),
      codeTypeSortieDenree: new FormControl(this.gefConnection?.properties?.importDenreeProperties?.typeSortie),
      codeDeclinaisonDenreeDeclinee: new FormControl(this.gefConnection?.properties?.importDenreeDeclineeProperties?.declinaison),
      codeUTDenreeDeclinee: new FormControl(this.gefConnection?.properties?.importDenreeDeclineeProperties?.uniteTechnique),
      codeTypeProduitDenreeDeclinee: new FormControl(this.gefConnection?.properties?.importDenreeDeclineeProperties?.typeProduit),
      codeZoneStockageDenreeDeclinee: new FormControl(this.gefConnection?.properties?.importDenreeDeclineeProperties?.zoneStockage),
      codeTypeApproDenreeDeclinee: new FormControl(this.gefConnection?.properties?.importDenreeDeclineeProperties?.typeAppro),
      codeTypeSortieDenreeDeclinee: new FormControl(this.gefConnection?.properties?.importDenreeDeclineeProperties?.typeSortie),
      codeUniteCommandeArticle: new FormControl(this.gefConnection?.properties?.importArticleProperties?.uniteCommande),
      codeUniteFacturationArticle: new FormControl(this.gefConnection?.properties?.importArticleProperties?.uniteFacturation),
      codeUniteStockageArticle: new FormControl(this.gefConnection?.properties?.importArticleProperties?.uniteStockage),
      codeAllergene: new FormControl(this.gefConnection?.properties?.importArticleProperties?.allergene),
      codeUFMagasin: new FormControl(this.gefConnection?.properties?.exportCommandesProperties?.ufMagasin),
      codeUFCuisine: new FormControl(this.gefConnection?.properties?.exportCommandesProperties?.ufCuisine),
      codeChorus: new FormControl(this.gefConnection?.properties?.exportCommandesProperties?.codeChorus),
      codeUFMagasinReceptions: new FormControl(this.gefConnection?.properties?.exportReceptionsProperties?.ufMagasin),
      codeUFCuisineReceptions: new FormControl(this.gefConnection?.properties?.exportReceptionsProperties?.ufCuisine),
      codeTaxeElementFacturable: new FormControl(this.gefConnection?.properties?.importElementFacturableProperties?.codeTaxe),
      codeTaxeFraisFixe: new FormControl(this.gefConnection?.properties?.importFraisFixeProperties?.codeTaxe),
      delaiPaiementPointFacturation: new FormControl(this.gefConnection?.properties?.importPointFacturationProperties?.delaiPaiement),
      synthetiquePointFacturation: new FormControl(this.gefConnection?.properties?.importPointFacturationProperties?.synthetique ?? false),
    });
  }

  initSelectedSecteurs(): void {
    this.selectedSecteurs = this.secteurList
      .filter(s => this.gefConnection?.properties?.importFournisseurProperties?.defautSecteurs?.some((ds: any) => ds.code === s.code))
      .map(s => s.id);
  }

  onSelectedSecteursChange($event: any): void {
    this.selectedSecteurs = $event;
  }

  initSelectedStatutFactureExterneList(): void {
    this.selectedStatutFactureExterneList = this.gefConnection?.properties?.exportFactureExterneProperties?.statutList ?? [];
  }

  onSelectedStatutFactureExterneListChange($event: any): void {
    this.selectedStatutFactureExterneList = $event;
  }

  initSelectedStatutAvoirExterneList(): void {
    this.selectedStatutAvoirExterneList = this.gefConnection?.properties?.exportAvoirExterneProperties?.statutList ?? [];
  }

  onSelectedStatutAvoirExterneListChange($event: any): void {
    this.selectedStatutAvoirExterneList = $event;
  }

  buildJsonProperties(): Record<string, any> {
    return {
      importFournisseurProperties: {
        defautSecteurs: this.secteurList.filter(s => this.selectedSecteurs.includes(s.id)),
      },
      importDenreeProperties: {
        famille: this.form.controls['codeFamilleDenree'].value,
        uniteTechnique: this.form.controls['codeUTDenree'].value,
        typeProduit: this.form.controls['codeTypeProduitDenree'].value,
        zoneStockage: this.form.controls['codeZoneStockageDenree'].value,
        typeAppro: this.form.controls['codeTypeApproDenree'].value,
        typeSortie: this.form.controls['codeTypeSortieDenree'].value,
      },
      importDenreeDeclineeProperties: {
        declinaison: this.form.controls['codeDeclinaisonDenreeDeclinee'].value,
        uniteTechnique: this.form.controls['codeUTDenreeDeclinee'].value,
        typeProduit: this.form.controls['codeTypeProduitDenreeDeclinee'].value,
        zoneStockage: this.form.controls['codeZoneStockageDenreeDeclinee'].value,
        typeAppro: this.form.controls['codeTypeApproDenreeDeclinee'].value,
        typeSortie: this.form.controls['codeTypeSortieDenreeDeclinee'].value,
      },
      importArticleProperties: {
        uniteCommande: this.form.controls['codeUniteCommandeArticle'].value,
        uniteFacturation: this.form.controls['codeUniteFacturationArticle'].value,
        uniteStockage: this.form.controls['codeUniteStockageArticle'].value,
        allergene: this.form.controls['codeAllergene'].value,
      },
      exportCommandesProperties: {
        ufMagasin: this.form.controls['codeUFMagasin'].value,
        ufCuisine: this.form.controls['codeUFCuisine'].value,
        codeChorus: this.form.controls['codeChorus'].value,
      },
      exportReceptionsProperties: {
        ufMagasin: this.form.controls['codeUFMagasinReceptions'].value,
        ufCuisine: this.form.controls['codeUFCuisineReceptions'].value,
      },
      exportFactureExterneProperties: {
        statutList: this.selectedStatutFactureExterneList,
      },
      exportAvoirExterneProperties: {
        statutList: this.selectedStatutAvoirExterneList,
      },
      importPointFacturationProperties: {
        delaiPaiement: this.form.controls['delaiPaiementPointFacturation'].value,
        synthetique: this.form.controls['synthetiquePointFacturation'].value,
      },
      importSiteProperties: {
        environnement: this.environnementLookup?.getSelectedItem()?.code ?? null,
      },
      importElementFacturableProperties: {
        codeTaxe: this.form.controls['codeTaxeElementFacturable'].value,
      },
      importFraisFixeProperties: {
        codeTaxe: this.form.controls['codeTaxeFraisFixe'].value,
      },
    };
  }
}
